import { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  ChevronDown,
  ChevronRight,
  Building2,
  TrendingUp,
  FileText,
  Banknote,
  Shield,
  Bell,
  LogOut,
  Settings,
  ChartColumn,
  Coins,
  Layers,
  CalendarDays,
  Search,
  MessageCircle,
  Tags,
  Database,
  ShieldCheck,
  Mail,
  Percent,
  UserCog,
} from "lucide-react";
import { useUser } from "@/context/useUser";
import { getUserInfo } from "@/lib/storeManager";
import finEaseHomeNavigationRoutes from "../../utils/navigationRoutes";
import { Input } from "@/common/components/ui";

const FinEaseAdminLayout = () => {
  const { isAuthenticated, logout } = useUser();
  const [userInfo, setUserInfo] = useState<any>(null);
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState({
    products: true,
    nps: false,
    ipo: false,
    screener: false,
    "physical gold": false,
    logs: false,
    cms: false,
    email: false,
    "role management": false,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const isActive = (path: string) => {
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  const menuItems = [
    {
      icon: LayoutDashboard,
      label: "Dashboard",
      path: finEaseHomeNavigationRoutes.ADMIN_DASHBOARD,
    },
    {
      icon: Users,
      label: "User Management",
      path: finEaseHomeNavigationRoutes.USER_MANAGEMENT,
    },
  ];

  const productItems = [
    {
      label: "NPS",
      icon: Building2,
      children: [
        {
          label: "NPS Users",
          icon: Users,
          path: finEaseHomeNavigationRoutes.NPS_USER_LIST,
        },
        {
          label: "Payment Entry",
          icon: Banknote,
          path: finEaseHomeNavigationRoutes.PAYMENT_ENTRY,
        },
        // {
        //   label: "Settlements",
        //   icon: Banknote,
        //   path: finEaseHomeNavigationRoutes.SETTLEMENTS,
        // },
      ],
    },
    {
      label: "IPO",
      icon: TrendingUp,
      children: [
        {
          label: "IPO Management",
          icon: Settings,
          path: finEaseHomeNavigationRoutes.IPO_MANAGEMENT,
        },
        {
          label: "Member Management",
          icon: Users,
          path: finEaseHomeNavigationRoutes.IPO_MEMBER_MANAGEMENT,
        },
        {
          label: "Applications",
          icon: FileText,
          path: finEaseHomeNavigationRoutes.IPO_APPLICATIONS,
        },
        // {
        //   label: "Analytics",
        //   icon: BarChart3,
        //   path: finEaseHomeNavigationRoutes.IPO_ANALYTICS,
        // },
        {
          label: "Past Orders",
          icon: Settings,
          path: finEaseHomeNavigationRoutes.IPO_PAST_ORDERS,
        },
        {
          label: "IPO Allotment",
          icon: Layers,
          path: finEaseHomeNavigationRoutes.IPO_ALLOTMENT,
        },
      ],
    },
    {
      label: "Screener",
      icon: ChartColumn,
      path: finEaseHomeNavigationRoutes.COMPANY_MASTER_LIST,
      children: [
        {
          label: "Company Master",
          icon: Building2,
          path: finEaseHomeNavigationRoutes.COMPANY_MASTER_LIST,
        },
        {
          label: "Category Master",
          icon: Tags,
          path: finEaseHomeNavigationRoutes.CATEGORY_MASTER,
        },
        {
          label: "Query Field Master",
          icon: Database,
          path: finEaseHomeNavigationRoutes.QUERY_FIELD_MASTER,
        },
        {
          label: "Query Master Admin",
          icon: ShieldCheck,
          path: finEaseHomeNavigationRoutes.QUERY_MASTER_ADMIN,
        },
        {
          label: "Query Master User",
          icon: Users,
          path: finEaseHomeNavigationRoutes.QUERY_MASTER_USER,
        },
      ],
    },
    {
      label: "Bonds",
      icon: FileText,
      path: finEaseHomeNavigationRoutes.BONDS,
    },
    {
      label: "Fixed Deposits",
      icon: Banknote,
      path: finEaseHomeNavigationRoutes.FIXED_DEPOSITS,
    },
    {
      label: "Physical Gold",
      icon: Coins,
      children: [
        // {
        //   label: "Gold Dashboard",
        //   icon: LayoutDashboard,
        //   path: finEaseHomeNavigationRoutes.PHYSICAL_GOLD_DASHBOARD,
        // },
        // {
        //   label: "Pricing Control",
        //   icon: TrendingUp,
        //   path: finEaseHomeNavigationRoutes.PHYSICAL_GOLD_PRICE_CONTROL,
        // },
        {
          label: "Product Master",
          icon: Layers,
          path: finEaseHomeNavigationRoutes.PHYSICAL_GOLD_PRODUCT_MASTER,
        },
        {
          label: "Delivery Master",
          icon: Settings,
          path: finEaseHomeNavigationRoutes.PHYSICAL_GOLD_DELIVERY_MASTER,
        },
        {
          label: "Gold Orders",
          icon: FileText,
          path: finEaseHomeNavigationRoutes.PHYSICAL_GOLD_ORDERS,
        },
        {
          label: "Gold Settlements",
          icon: Building2,
          path: finEaseHomeNavigationRoutes.PHYSICAL_GOLD_SETTLEMENTS,
        },
        {
          label: "Commission",
          icon: Percent,
          path: finEaseHomeNavigationRoutes.PHYSICAL_GOLD_COMMISSION,
        },
      ],
    },
    {
      label: "Contact Us",
      icon: MessageCircle,
      path: finEaseHomeNavigationRoutes.CONTACT_QUERIES,
    },
  ];

  const systemItems = [
    {
      label: "Logs",
      icon: Users,
      children: [
        {
          label: "User Audit Logs",
          icon: FileText,
          path: finEaseHomeNavigationRoutes.ACTIVITY_LOG,
        },
        {
          label: "API Issue Logs",
          icon: Shield,
          path: finEaseHomeNavigationRoutes.API_ISSUE_LOG,
        },
        {
          label: "Login Activity",
          icon: Shield,
          path: finEaseHomeNavigationRoutes.LOGIN_ACTIVITY_LOG,
        },
        {
          label: "SMS Logs",
          icon: MessageCircle,
          path: finEaseHomeNavigationRoutes.SMS_ACTIVITY_LOG,
        },
      ],
    },
    {
      icon: UserCog,
      label: "Role Management",
      children: [
        {
          label: "Users",
          icon: Users,
          path: finEaseHomeNavigationRoutes.ROLE_MANAGEMENT_USERS,
        },
        {
          label: "Roles",
          icon: ShieldCheck,
          path: finEaseHomeNavigationRoutes.ROLE_MANAGEMENT_ROLES,
        },
      ],
    },
  ];

  const cmsItems: Array<{
    label: string;
    icon: any;
    path?: string;
    children?: Array<{
      label: string;
      icon: any;
      path: string;
    }>;
  }> = [
    {
      label: "CMS",
      icon: Layers,
      children: [
        {
          label: "Banners",
          icon: FileText, // Or ImageIcon if we add it
          path: finEaseHomeNavigationRoutes.CMS_BANNERS,
        },
        {
          label: "Holidays",
          icon: CalendarDays,
          path: finEaseHomeNavigationRoutes.CMS_HOLIDAYS,
        },
      ],
    },
  ];

  const emailItems: Array<{
    label: string;
    icon: any;
    children?: Array<{
      label: string;
      icon: any;
      path: string;
    }>;
  }> = [
    {
      label: "Email",
      icon: Mail,
      children: [
        {
          label: "Website",
          icon: MessageCircle,
          path: finEaseHomeNavigationRoutes.EMAIL_WEBSITE_LEADS,
        },
      ],
    },
  ];

  useEffect(() => {
    const userInfoData = getUserInfo();
    setUserInfo(userInfoData);
  }, [isAuthenticated]);

  useEffect(() => {
    productItems.forEach(item => {
      if (item.children?.some(child => isActive(child.path))) {
        setExpandedSections(prev => ({
          ...prev,
          [item.label.toLowerCase()]: true,
        }));
      }
    });

    systemItems.forEach(item => {
      if (item.children?.some(child => isActive(child.path))) {
        setExpandedSections(prev => ({
          ...prev,
          [item.label.toLowerCase()]: true,
        }));
      }
    });

    cmsItems.forEach(item => {
      if (item.children?.some(child => isActive(child.path))) {
        setExpandedSections(prev => ({
          ...prev,
          [item.label.toLowerCase()]: true,
        }));
      }
    });

    emailItems.forEach(item => {
      if (item.children?.some(child => isActive(child.path))) {
        setExpandedSections(prev => ({
          ...prev,
          [item.label.toLowerCase()]: true,
        }));
      }
    });
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="flex w-[270px] flex-col border-r border-gray-200 bg-white shadow-[2px_0_8px_rgb(0_0_0/2%)]">
        {/* Logo Section */}
        <div className="border-b border-gray-200 px-12 py-4">
          <Link
            to={finEaseHomeNavigationRoutes.ADMIN_DASHBOARD}
            className="flex items-center gap-2"
          >
            <img
              src="/assets/logoooo.svg"
              alt="logo"
              className="h-10 w-auto max-w-full"
            />
          </Link>
        </div>

        {/* Navigation Menu */}
        <nav className="sidebar-scroll flex-1 overflow-y-auto py-4">
          {/* Main Menu Items */}
          <div className="space-y-0.5 px-2">
            {menuItems.map(item => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors ${
                    active
                      ? "bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 font-medium text-white"
                      : "text-gray-700 hover:bg-[#66A0CD]/5 hover:text-brandDark"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="text-sm font-normal">{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* CMS Section */}
          <div className="mt-6 px-2">
            <div className="mb-3 px-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-brandPrimary">
                CMS
              </span>
            </div>
            <div className="space-y-0.5">
              {cmsItems.map(item => {
                if (item.children) {
                  const sectionKey = item.label.toLowerCase();
                  const isExpanded =
                    expandedSections[
                      sectionKey as keyof typeof expandedSections
                    ];
                  const hasActiveChild = item.children.some(child =>
                    isActive(child.path)
                  );
                  const Icon = item.icon;

                  return (
                    <div key={item.label}>
                      <button
                        onClick={() =>
                          toggleSection(
                            sectionKey as keyof typeof expandedSections
                          )
                        }
                        className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 transition-colors ${
                          hasActiveChild
                            ? "bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 font-medium text-white"
                            : "text-gray-700 hover:bg-[#66A0CD]/5 hover:text-brandDark"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className="h-5 w-5" />
                          <span className="text-sm font-normal">
                            {item.label}
                          </span>
                        </div>
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                      {isExpanded && (
                        <div className="ml-4 mt-0.5 space-y-0.5 border-l-2 border-gray-200 pl-3">
                          {item.children.map(child => {
                            const active = isActive(child.path);
                            const Icon = child.icon;
                            return (
                              <Link
                                key={child.path}
                                to={child.path}
                                className={`block rounded-lg px-4 py-2 transition-colors ${
                                  active
                                    ? "bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 font-medium text-white"
                                    : "text-gray-600 hover:bg-[#66A0CD]/5 hover:text-brandDark"
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <Icon className="h-4 w-4" />
                                  <span className="text-sm font-normal">
                                    {child.label}
                                  </span>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                } else {
                  const Icon = item.icon;
                  const active = isActive(item.path ?? "");
                  return (
                    <Link
                      key={item.label}
                      to={item.path ?? ""}
                      className={`flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors ${
                        active
                          ? "bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 font-medium text-white"
                          : "text-gray-700 hover:bg-[#66A0CD]/5 hover:text-brandDark"
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="text-sm font-normal">{item.label}</span>
                    </Link>
                  );
                }
              })}
            </div>
          </div>

          {/* Email Section */}
          <div className="mt-6 px-2">
            <div className="mb-3 px-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-brandPrimary">
                Email
              </span>
            </div>
            <div className="space-y-0.5">
              {emailItems.map(item => {
                if (item.children) {
                  const sectionKey = item.label.toLowerCase();
                  const isExpanded =
                    expandedSections[
                      sectionKey as keyof typeof expandedSections
                    ];
                  const hasActiveChild = item.children.some(child =>
                    isActive(child.path)
                  );
                  const Icon = item.icon;

                  return (
                    <div key={item.label}>
                      <button
                        onClick={() =>
                          toggleSection(
                            sectionKey as keyof typeof expandedSections
                          )
                        }
                        className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 transition-colors ${
                          hasActiveChild
                            ? "bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 font-medium text-white"
                            : "text-gray-700 hover:bg-[#66A0CD]/5 hover:text-brandDark"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className="h-5 w-5" />
                          <span className="text-sm font-normal">
                            {item.label}
                          </span>
                        </div>
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                      {isExpanded && (
                        <div className="ml-4 mt-0.5 space-y-0.5 border-l-2 border-gray-200 pl-3">
                          {item.children.map(child => {
                            const active = isActive(child.path);
                            const ChildIcon = child.icon;
                            return (
                              <Link
                                key={child.path}
                                to={child.path}
                                className={`block rounded-lg px-4 py-2 transition-colors ${
                                  active
                                    ? "bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 font-medium text-white"
                                    : "text-gray-600 hover:bg-[#66A0CD]/5 hover:text-brandDark"
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <ChildIcon className="h-4 w-4" />
                                  <span className="text-sm font-normal">
                                    {child.label}
                                  </span>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </div>

          {/* PRODUCTS Section */}
          <div className="mt-6 px-2">
            <div className="mb-3 px-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-brandPrimary">
                PRODUCTS
              </span>
            </div>
            <div className="space-y-0.5">
              {productItems.map(item => {
                if (item.children) {
                  const sectionKey = item.label.toLowerCase();
                  const isExpanded =
                    expandedSections[
                      sectionKey as keyof typeof expandedSections
                    ];
                  const hasActiveChild = item.children.some(child =>
                    isActive(child.path)
                  );
                  const Icon = item.icon;

                  return (
                    <div key={item.label}>
                      <button
                        onClick={() =>
                          toggleSection(
                            sectionKey as keyof typeof expandedSections
                          )
                        }
                        className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 transition-colors ${
                          hasActiveChild
                            ? "bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 font-medium text-white"
                            : "text-gray-700 hover:bg-[#66A0CD]/5 hover:text-brandDark"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className="h-5 w-5" />
                          <span className="text-sm font-normal">
                            {item.label}
                          </span>
                        </div>
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                      {isExpanded && (
                        <div className="ml-4 mt-0.5 space-y-0.5 border-l-2 border-gray-200 pl-3">
                          {item.children.map(child => {
                            const active = isActive(child.path);
                            const Icon = child.icon;
                            return (
                              <Link
                                key={child.path}
                                to={child.path}
                                className={`block rounded-lg px-4 py-2 transition-colors ${
                                  active
                                    ? "bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 font-medium text-white"
                                    : "text-gray-600 hover:bg-[#66A0CD]/5 hover:text-brandDark"
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <Icon className="h-4 w-4" />
                                  <span className="text-sm font-normal">
                                    {child.label}
                                  </span>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                } else {
                  const Icon = item.icon;
                  const active = isActive(item.path ?? "");
                  return (
                    <Link
                      key={item.path}
                      to={item.path ?? ""}
                      className={`flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors ${
                        active
                          ? "bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 font-medium text-white"
                          : "text-gray-700 hover:bg-[#66A0CD]/5 hover:text-brandDark"
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="text-sm font-normal">{item.label}</span>
                    </Link>
                  );
                }
              })}
            </div>
          </div>

          {/* SYSTEM Section */}
          <div className="mt-6 px-2">
            <div className="mb-3 px-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-brandPrimary">
                SYSTEM
              </span>
            </div>
            <div className="space-y-0.5">
              {systemItems.map(item => {
                const sectionKey = item.label.toLowerCase();
                const isExpanded =
                  expandedSections[sectionKey as keyof typeof expandedSections];
                const hasActiveChild = item.children.some(child =>
                  isActive(child.path)
                );
                const Icon = item.icon;

                return (
                  <div key={item.label}>
                    <button
                      type="button"
                      onClick={() =>
                        toggleSection(
                          sectionKey as keyof typeof expandedSections
                        )
                      }
                      className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 transition-colors ${
                        hasActiveChild
                          ? "bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 font-medium text-white"
                          : "text-gray-700 hover:bg-[#66A0CD]/5 hover:text-brandDark"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="h-5 w-5" />
                        <span className="text-sm font-normal">
                          {item.label}
                        </span>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                    {isExpanded && (
                      <div className="ml-4 mt-0.5 space-y-0.5 border-l-2 border-gray-200 pl-3">
                        {item.children.map(child => {
                          const active = isActive(child.path);
                          const ChildIcon = child.icon;
                          return (
                            <Link
                              key={child.path}
                              to={child.path}
                              className={`block rounded-lg px-4 py-2 transition-colors ${
                                active
                                  ? "bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 font-medium text-white"
                                  : "text-gray-600 hover:bg-[#66A0CD]/5 hover:text-brandDark"
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <ChildIcon className="h-4 w-4" />
                                <span className="text-sm font-normal">
                                  {child.label}
                                </span>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </nav>

        {/* Bottom Profile Section */}
        <div className="space-y-3 border-t border-gray-200 p-4">
          {/* Profile Box */}
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brandDark font-semibold text-white">
              SA
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-800">
                Super Admin
              </div>
              <div className="truncate text-xs text-gray-500">
                {userInfo?.email}
              </div>
            </div>
            <button className="shrink-0 rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700">
              <Bell className="h-5 w-5" />
            </button>
          </div>

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-red-600 transition-colors hover:bg-red-50"
          >
            <LogOut className="h-4 w-4" />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="sticky top-0 z-10 flex items-center justify-end border-b border-gray-200 bg-white px-8 py-4">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary" />
              <Input
                placeholder="Search..."
                className="w-[200px] bg-gray-50 pl-10"
              />
            </div>
            <button className="relative shrink-0 rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700">
              <Bell className="h-5 w-5" />
              <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-red-500" />
            </button>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default FinEaseAdminLayout;
